<template>
  <ion-page class="modal-primary media-modal-page" @pointerdown="onPointerDown" @pointerup="onPointerUp">
    <ion-content :force-overscroll="!isWeb" class="media-modal-content">
      <div class="options" @pointerdown.stop @pointerup.stop @click.stop>
        <img :src="closeIcon" class="close-icon" @click.stop="dismiss" />

        <img v-if="files.every(f => getViewerType(f.type) === 'image')" @click="carousel?.rotate"
          :src="rotationIcon" />

        <img v-if="showDownloadIcon && currentFile" :src="downloadIcon" @click.stop="downloadCurrent" />
      </div>

      <WksMediaModalCarousel ref="carousel" :files="files" :zoomable="true" :initialSlide="index"
        :enableWebCarousel="enableWebCarousel" />
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { IonContent, modalController, IonPage } from "@ionic/vue";
import { ref, onMounted, computed, toRefs } from "vue";
import { IFont } from "@/helpers/fonts";
import { getIcon } from "@/helpers/api";
import { IFile } from "@/modules/a.worksheets/stores/worksheetDataStore";
import WksMediaModalCarousel from "@/modules/a.worksheets/views/components/WksMediaModalCarousel.vue";
import { IFileResolved } from "@/modules/a.worksheets/helpers/useIFileResolver";
import { getViewerType, mimeTypeToExtension } from "@/globals/helpers/media/mimeTypeUtils";
import { useDeviceStatus } from "@/stores/deviceStatus";


const { isWeb } = toRefs(useDeviceStatus());

const props = defineProps({
  modalBoxTitle: String,
  selectedValue: String,
  rightButtonLabel: String,
  middleButtonLabel: String,
  leftButtonLabel: String,
  files: { type: Array<IFileResolved>, default: () => null },
  index: { type: Number, default: 0 },
  showDownloadIcon: { type: Boolean, default: false },
  enableWebCarousel: { type: Boolean, default: false },
});

const dismiss = () => modalController.dismiss(null, "close");
const leftArrow = ref<string>();
const closeIcon = ref<string>();
const rotationIcon = ref<string>();
const downloadIcon = ref<string>();
const carousel = ref<InstanceType<typeof WksMediaModalCarousel> | null>(null);
onMounted(async () => {
  console.log("MODAL:", props.files)
  leftArrow.value = await getIcon("1010", "left_arrow");
  closeIcon.value = await getIcon("1010", "close");
  rotationIcon.value = await getIcon("1010", "rotate-right");
  downloadIcon.value = await getIcon("2105", "download");
});

const currentFile = computed(() => props.files?.[props.index] ?? props.files?.[0]);

async function downloadCurrent() {
  const file = currentFile.value;
  if (!file?.source) return;

  try {
    const response = await fetch(file.source);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const extension = file.type ? mimeTypeToExtension[file.type] : undefined;

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = extension ? `download.${extension}` : "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Erro ao transferir o ficheiro:", error);
  }
}

const pointerStart = ref<{ x: number; y: number } | null>(null);

function onPointerDown(e: PointerEvent) {
  pointerStart.value = {
    x: e.clientX,
    y: e.clientY,
  };
}

function onPointerUp(e: PointerEvent) {
  if (!pointerStart.value) return;

  const diffX = Math.abs(e.clientX - pointerStart.value.x);
  const diffY = Math.abs(e.clientY - pointerStart.value.y);

  pointerStart.value = null;

  const moved = diffX > 8 || diffY > 8;

  if (moved) return;

  dismiss();
}
</script>

<style scoped>
ion-title {
  text-align: center;
  width: 100%;
  height: 100%;
  top: 0;
  position: absolute;
}

ion-toolbar {
  padding: 0 5%;
  --ion-safe-area-top: 8%;
}

.options {
  position: absolute;
  top: 4%;
  right: 4%;
  z-index: 100;
  display: flex;
  gap: 2%;
  align-items: center;
  width: 100%;
  padding: 2%;
  box-sizing: border-box;
  flex-direction: row-reverse;
}

.options>* {
  box-sizing: content-box;
  width: 8%;
  max-width: 30px;
  aspect-ratio: 1 / 1;
  cursor: pointer;

  background-color: rgba(255, 255, 255, 0.75);
  padding: 1%;
  border-radius: 50%;

  user-drag: none;
  -webkit-user-drag: none;
  user-select: none;
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
}

.options>*:hover {
  transform: scale(1.1);
  background-color: rgba(255, 255, 255, 1);
}

.web .close-icon {
  width: 3%;
}

.media-modal-page,
.media-modal-content,
.media-modal-page :deep(.swiper),
.media-modal-page :deep(.swiper-slide),
.media-modal-page :deep(img) {
  cursor: zoom-out;
}

.options,
.options > *,
.options img {
  cursor: pointer !important;
}
</style>
